import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { refreshAuthorizationHeader } from '$shared/auth/auth.utils';
import { LOCALHOST_BASE_URL } from '$shared/utils/api-paths';

import { environment } from '../../environment';

const configAxios: AxiosRequestConfig = {
  baseURL: environment.apiUrl || LOCALHOST_BASE_URL,
  timeout: 500 * 1000,
};

export const AxiosInstance = axios.create(configAxios);
async function redirectToLogin(): Promise<void> {
  try {
    await signInWithRedirect();
  } catch (federatedSignInError) {
    // eslint-disable-next-line no-console -- maybe useful for debugging
    console.error(federatedSignInError);
  }
}

async function errorHandling(error: unknown): Promise<never> {
  // eslint-disable-next-line no-console -- maybe useful for debugging
  console.error(error);
  await redirectToLogin();
  return Promise.reject(error);
}

AxiosInstance.interceptors.request.use(async (request) => {
  try {
    const authorizationHeader = await refreshAuthorizationHeader();
    request.headers.Authorization = authorizationHeader;
    return request;
  } catch (error) {
    return errorHandling(error);
  }
});

AxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    await signOut();
    return Promise.reject(error);
  },
);

export const fetcher = {
  async get<T>(url: string, baseURL?: string): Promise<T> {
    const res = await AxiosInstance.get<T>(url, { ...configAxios, baseURL });
    return res.data;
  },

  post<T, R>(url: string, data: T): Promise<AxiosResponse<R>> {
    return AxiosInstance.post<T, AxiosResponse<R>>(url, data);
  },

  async patch<T, R>(url: string, data: T): Promise<R> {
    const res = await AxiosInstance.patch<T, AxiosResponse<R>>(url, data);
    return res.data;
  },

  delete<T>(url: string): Promise<AxiosResponse<T>> {
    return AxiosInstance.delete<T>(url);
  },
};
